import React, { useEffect, useRef } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Illustration } from '../common/Illustration';
import { useUiSound } from '../../hooks/useUiSound';

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state-view" role="status" aria-live="polite">
      <div className="orbital-loader" aria-hidden="true">
        <span className="orbit-ring ring-outer" />
        <span className="orbit-ring ring-inner" />
        <Loader2 className="state-spinner" size={22} />
      </div>
      <p>{label}</p>
    </div>
  );
}

export function ErrorState({
  message = 'Something went wrong.',
  onRetry,
  illustrationContext = 'error.network',
  playSound = true,
}: {
  message?: string;
  onRetry?: () => void;
  illustrationContext?: string;
  playSound?: boolean;
}) {
  const { play } = useUiSound();
  const played = useRef(false);

  useEffect(() => {
    if (playSound === false || played.current) return;
    played.current = true;
    play('error');
  }, [play, playSound]);

  return (
    <div className="state-view" role="alert">
      <Illustration context={illustrationContext} size="lg" className="state-illustration" />
      <AlertCircle className="state-icon is-error visually-hidden" size={30} aria-hidden="true" />
      <p>{message}</p>
      {onRetry && (
        <button type="button" className="secondary-action compact" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title = 'Nothing here yet',
  message,
  illustrationContext = 'empty.default',
  action,
}: {
  title?: string;
  message?: string;
  /** Registry context — see config/illustrations.ts */
  illustrationContext?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="state-view">
      <Illustration context={illustrationContext} size="lg" className="state-illustration" />
      <strong>{title}</strong>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}
